// @ts-strict-ignore
import React, { useMemo } from 'react'
import { useMediaQuery } from '@mantine/hooks'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'
import { Box, Icon } from '@node-space/ui-components'
import { routeConfig } from './configuration'

export type BreadcrumbProps = {
  customConfig?: CustomBreadCrumbConfig[]
}

export type CustomBreadCrumbConfig = {
  title: string
  link?: string
}

type BreadCrumb = {
  title?: string
  link?: string
  showSeparator?: boolean
}

const Breadcrumbs = ({ customConfig }: BreadcrumbProps) => {
  const location = useLocation()
  const { t } = useTranslation()
  const isMobileScreen = useMediaQuery('(max-width: 640px)')

  const breadcrumbs = useMemo(() => {
    const pathArray = location.pathname.split('/').filter(crumb => !!crumb)
    const finalBreadcrumbs: BreadCrumb[] = []

    if (customConfig) {
      customConfig.forEach((breadcrumbItem, index) => {
        finalBreadcrumbs.push({ title: t(breadcrumbItem.title), link: breadcrumbItem?.link })

        if (customConfig.length > index + 1) {
          finalBreadcrumbs.push({ showSeparator: true }) // Separator
        }
      })
    } else {
      pathArray.forEach(crumb => {
        const breadcrumbConfig = routeConfig[`/${crumb}`]

        if (breadcrumbConfig) {
          // Incorporate predefined breadcrumb items if they exist
          if (breadcrumbConfig.breadcrumbs) {
            breadcrumbConfig.breadcrumbs.forEach(breadcrumbItem => {
              finalBreadcrumbs.push(breadcrumbItem)
              finalBreadcrumbs.push({ showSeparator: true }) // Separator
            })
          }

          // Add current breadcrumb
          finalBreadcrumbs.push({ title: breadcrumbConfig.title, link: null })
        }
      })
    }

    return finalBreadcrumbs
  }, [location.pathname, customConfig])

  if (!breadcrumbs.length) {
    return null
  }

  if (isMobileScreen) {
    return (
      <Box flex>
        <h2 className="text-h2 text-foreground-prominent">
          {t(breadcrumbs[breadcrumbs.length - 1]?.title)}
        </h2>
      </Box>
    )
  }

  return (
    <Box flex align="center">
      {breadcrumbs.map(cr => (
        <Box key={cr?.title} flex>
          {cr?.showSeparator ? (
            <Box flex align="center" paddingX={20} paddingY={12} className="text-foreground-faint">
              <Icon size="large" name="CaretRight" />
            </Box>
          ) : (
            cr?.title && (
              <h2 className="text-h2 text-foreground-prominent">
                {cr?.link ? <Link to={cr.link}>{t(cr.title)}</Link> : t(cr.title)}
              </h2>
            )
          )}
        </Box>
      ))}
    </Box>
  )
}

export default Breadcrumbs
